/**
 * Custom hook for detecting device shake gestures
 * Handles motion sensor permissions and shake event detection
 * Platform-agnostic implementation supporting iOS and Android
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { SHAKE_THRESHOLD, SHAKE_TIMEOUT_MS } from '../lib/constants';
import type { PermissionState } from '../types';

interface UseShakeDetectionOptions {
  onShake: () => void;
  threshold?: number;
  timeout?: number;
  enabled?: boolean;
}

export function useShakeDetection({
  onShake,
  threshold = SHAKE_THRESHOLD,
  timeout = SHAKE_TIMEOUT_MS,
  enabled = true,
}: UseShakeDetectionOptions) {
  const [isSupported, setIsSupported] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState>('prompt');

  // Track last shake time to prevent rapid repeated triggers
  const lastShakeTime = useRef(0);
  // Store previous acceleration values to calculate delta
  const lastX = useRef(0);
  const lastY = useRef(0);
  const lastZ = useRef(0);

  /**
   * Verify device motion API availability
   */
  const checkSupport = useCallback(() => {
    const supported =
      typeof window !== 'undefined' &&
      'DeviceMotionEvent' in window &&
      typeof DeviceMotionEvent !== 'undefined';
    setIsSupported(supported);
    return supported;
  }, []);

  /**
   * Request motion sensor permissions
   * iOS 13+ requires explicit user permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!checkSupport()) {
      setPermissionState('denied');
      return false;
    }

    // iOS 13+ requires explicit permission
    if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
      try {
        const permission = await (DeviceMotionEvent as any).requestPermission();
        const granted = permission === 'granted';
        setPermissionState(granted ? 'granted' : 'denied');
        return granted;
      } catch {
        setPermissionState('denied');
        return false;
      }
    } else {
      // Android and older iOS auto-grant permission
      setPermissionState('granted');
      return true;
    }
  }, [checkSupport]);

  /**
   * Process motion events and detect shake gestures
   * Calculates acceleration delta across all axes
   */
  const handleMotion = useCallback(
    (event: DeviceMotionEvent) => {
      if (!enabled) return;

      const current = Date.now();
      // Prevent rapid repeated triggers within timeout period
      if (current - lastShakeTime.current < timeout) {
        return;
      }

      const acceleration = event.accelerationIncludingGravity;
      // Validate acceleration data exists
      if (
        !acceleration ||
        acceleration.x === null ||
        acceleration.y === null ||
        acceleration.z === null
      ) {
        return;
      }

      // Calculate change in acceleration across all axes
      const deltaX = Math.abs(acceleration.x - lastX.current);
      const deltaY = Math.abs(acceleration.y - lastY.current);
      const deltaZ = Math.abs(acceleration.z - lastZ.current);

      // Trigger shake if any axis exceeds threshold
      if (deltaX > threshold || deltaY > threshold || deltaZ > threshold) {
        lastShakeTime.current = current;
        onShake();
      }

      // Store current values for next comparison
      lastX.current = acceleration.x;
      lastY.current = acceleration.y;
      lastZ.current = acceleration.z;
    },
    [onShake, threshold, timeout, enabled]
  );

  useEffect(() => {
    checkSupport();
  }, [checkSupport]);

  // Attach motion listener only when supported, enabled, and permitted
  useEffect(() => {
    if (!isSupported || !enabled || permissionState !== 'granted') {
      return;
    }

    window.addEventListener('devicemotion', handleMotion);
    return () => {
      window.removeEventListener('devicemotion', handleMotion);
    };
  }, [isSupported, enabled, permissionState, handleMotion]);

  return {
    isSupported,
    permissionState,
    requestPermission,
  };
}

